import { Routes, Route, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect, useCallback, createContext, useContext } from 'react'
import {
  LayoutDashboard,
  ChefHat,
  ClipboardList,
  Package,
  BookOpen,
  Layers,
  Menu as MenuIcon,
  X,
  ChevronDown,
  Globe,
  Command,
  Settings,
  Mic,
  Timer,
  Calculator,
  Sparkles,
  Loader2,
  UtensilsCrossed,
  Scale,
  SlidersHorizontal,
  ListChecks,
  Wrench,
  Printer,
  Lock,
} from 'lucide-react'

// Pages
import OperationsHome from './pages/OperationsHome'
import MenuBuilder from './pages/MenuBuilder'
import MealCounts from './pages/MealCounts'
import Plated from './pages/Plated'
import { PLATED_CITIES } from './lib/plated'
import Ordering from './pages/Ordering'
import Recipes from './pages/Recipes'
import ComponentLibrary from './pages/ComponentLibrary'
import MenuDevelopment from './pages/MenuDevelopment'
import Plating from './pages/Plating'
import Weights from './pages/Weights'
import Customs from './pages/Customs'
import Prep from './pages/Prep'
import Kitchen from './pages/Kitchen'
import AdminKitchen from './pages/AdminKitchen'
import CustomerLabels from './pages/CustomerLabels'
import PrintMaterials from './pages/PrintMaterials'

// Components
import { Toast, ToastProvider, useToast } from './components/Toast'
import { menusAPI, translateAPI } from './lib/api'
import { trLabel } from './lib/translations'
import { cityFromHost, initialCity, CORE_CITIES, PARTNER_CITIES } from './lib/cityWorkspace'
import CommandPalette from './components/CommandPalette'
import VoiceAssistant from './components/VoiceAssistant'
import KitchenTimers from './components/KitchenTimers'
import BatchCalculator from './components/BatchCalculator'
import { InputModal } from './components/ConfirmModal'

// ══════════════════════════════════════════════════════════════════════════════
// APP CONTEXT
// ══════════════════════════════════════════════════════════════════════════════

const AppContext = createContext(null)

export function useApp() {
  return useContext(AppContext)
}

// ══════════════════════════════════════════════════════════════════════════════
// SIDEBAR NAVIGATION
// ══════════════════════════════════════════════════════════════════════════════

const navigation = [
  { name: 'This Week', href: '/', icon: LayoutDashboard },
  { name: 'Meal Counts', href: '/meal-counts', icon: ClipboardList },
  { name: 'Plated', href: '/plated', icon: Lock, cities: PLATED_CITIES },
  { name: 'Ordering', href: '/ordering', icon: Package },
  { name: 'Kitchen', href: '/kitchen-overview', icon: UtensilsCrossed },
  { name: 'Recipes', href: '/recipes', icon: BookOpen },
]

const advancedNavigation = [
  { name: 'Menu Builder', href: '/menu-builder', icon: ChefHat },
  { name: 'Prep Manager', href: '/prep', icon: ListChecks },
  { name: 'Plating', href: '/plating', icon: UtensilsCrossed },
  { name: 'Weights', href: '/weights', icon: Scale },
  { name: 'Customs', href: '/customs', icon: SlidersHorizontal },
  { name: 'Components', href: '/components', icon: Layers },
  { name: 'Menu Dev', href: '/menu-development', icon: Sparkles },
  { name: 'Print Materials', href: '/print-materials', icon: Printer },
]

function CityPicker({ cities, onPick }) {
  // Starter / landing screen — the only place a city is chosen. Falls back to the
  // two known cities if the list hasn't loaded from the API yet.
  const options = cities.length ? cities : CORE_CITIES
  return (
    <div className="min-h-screen bg-havn-green-900 flex flex-col items-center justify-center px-6">
      <img src="/logo.png" alt="Havn Club" className="h-20 w-auto mb-1" />
      <p className="text-xs text-havn-brass-400 font-medium tracking-widest uppercase mb-12">
        Culinary Ops
      </p>
      <h1 className="font-display text-2xl md:text-3xl font-semibold text-white mb-1.5">
        Select your kitchen
      </h1>
      <p className="text-sm text-white/50 mb-10">Choose a city to continue</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 w-full max-w-xl">
        {options.map(city => (
          <button
            key={city.id}
            onClick={() => onPick(city.id)}
            className="group flex flex-col items-center justify-center gap-3 px-8 py-12 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-havn-brass-400 rounded-2xl transition-all duration-150"
          >
            <Globe className="w-8 h-8 text-havn-brass-400 group-hover:scale-110 transition-transform" />
            <span className="font-display text-xl font-semibold text-white">{city.name}</span>
            <span className="text-[10px] uppercase tracking-widest text-white/40">{city.id.toUpperCase()}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

function MenuSelector({ currentMenu, menus, onMenuChange, onCreateMenu }) {
  const [open, setOpen] = useState(false)

  const current = menus.find(m => m.id === currentMenu)

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 rounded-lg transition-colors w-full"
      >
        <span className="flex-1 text-left">
          <span className="text-white/50 text-xs block">Current Menu</span>
          <span className="text-white font-medium">{current?.name || 'No menu selected'}</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 right-0 mt-1 bg-havn-green-800 rounded-lg shadow-lg border border-white/10 overflow-hidden z-20 max-h-64 overflow-y-auto">
            {menus.length === 0 ? (
              <div className="px-3 py-4 text-center text-white/50 text-sm">
                No menus yet
              </div>
            ) : (
              menus.map(menu => (
                <button
                  key={menu.id}
                  onClick={() => { onMenuChange(menu.id); setOpen(false) }}
                  className={`w-full px-3 py-2 text-sm text-left hover:bg-white/10 transition-colors
                    ${menu.id === currentMenu ? 'text-white bg-white/5' : 'text-white/70'}`}
                >
                  <span className="block font-medium">{menu.name}</span>
                  <span className="text-xs text-white/40">
                    {menu.status} • {menu.dish_count || 0} dishes
                  </span>
                </button>
              ))
            )}
            <button
              onClick={() => { onCreateMenu(); setOpen(false) }}
              className="w-full px-3 py-2 text-sm text-left text-havn-brass-400 hover:bg-white/10 border-t border-white/10"
            >
              + Create New Menu
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function Sidebar({ isOpen, onClose, currentCity, cities, onGoHome, currentMenu, menus, onMenuChange, onCreateMenu, hostedCity }) {
  const location = useLocation()
  const [advancedOpen, setAdvancedOpen] = useState(() => advancedNavigation.some((item) => location.pathname === item.href))

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-72 bg-havn-green-900 flex flex-col
          transform transition-transform duration-200 ease-in-out
          lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Brand Header */}
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center justify-between mb-4">
            <button onClick={onGoHome} title={hostedCity ? "Back to dashboard" : "Switch city — back to start"} className="text-left focus:outline-none">
              <img
                src="/logo.png"
                alt="Havn Club"
                className="h-14 w-auto"
              />
              <p className="text-xs text-havn-brass-400 mt-1 font-medium tracking-widest uppercase">
                Culinary Ops
              </p>
            </button>
            <button
              onClick={onClose}
              className="lg:hidden p-1 text-white/60 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Current city — read-only; click the logo or "Switch" to change it */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5">
            <Globe className="w-4 h-4 text-havn-brass-400" />
            <span className="text-sm font-medium text-white flex-1">
              {cities.find(c => c.id === currentCity)?.name || currentCity?.toUpperCase()}
            </span>
            {!hostedCity && <button
              onClick={onGoHome}
              className="text-[10px] uppercase tracking-widest text-white/40 hover:text-white transition-colors"
            >
              Switch
            </button>}
          </div>
        </div>

        {/* Menu Selector */}
        <div className="px-4 py-3 border-b border-white/10">
          <MenuSelector
            currentMenu={currentMenu}
            menus={menus}
            onMenuChange={onMenuChange}
            onCreateMenu={onCreateMenu}
          />
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
          {navigation.filter((item) => !item.cities || item.cities.includes(currentCity)).map((item) => {
            const isActive = location.pathname === item.href ||
              (item.href !== '/' && location.pathname.startsWith(item.href))
            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={onClose}
                className={`nav-item ${isActive ? 'active' : ''}`}
              >
                <item.icon className="w-5 h-5" />
                {item.name}
              </NavLink>
            )
          })}
          <div className="pt-3 mt-3 border-t border-white/10">
            <button
              onClick={() => setAdvancedOpen((open) => !open)}
              aria-expanded={advancedOpen}
              className="nav-item w-full"
            >
              <Wrench className="w-5 h-5" />
              <span className="flex-1 text-left">Advanced</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
            </button>
            {advancedOpen && advancedNavigation.map((item) => {
              const isActive = location.pathname === item.href ||
                (item.href !== '/' && location.pathname.startsWith(item.href))
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={onClose}
                  className={`nav-item ml-3 ${isActive ? 'active' : ''}`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.name}
                </NavLink>
              )
            })}
          </div>
        </nav>

        {/* Keyboard Shortcut Hint */}
        <div className="px-4 py-3 border-t border-white/10">
          <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg text-xs text-white/70">
            <Command className="w-3.5 h-3.5" />
            <span>Press <kbd className="px-1 py-0.5 bg-white/10 rounded">⌘K</kbd> for quick search</span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400" />
            <span className="text-xs text-white/50">Culinary App v2.0</span>
          </div>
        </div>
      </aside>
    </>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// PAGE HEADER
// ══════════════════════════════════════════════════════════════════════════════

const pageTitles = {
  '/': { title: 'This Week', subtitle: 'Weekly setup, counts, containers, and print center' },
  '/kitchen': { title: 'Kitchen', subtitle: 'Shared iPad workflow for prep, cooking, weighing, and plating' },
  '/kitchen-overview': { title: 'Kitchen', subtitle: 'Production progress, weights, plating, and items needing attention' },
  '/chef': { title: 'Chef', subtitle: 'Recipes, cooking checkoffs, and cooked quantities by category' },
  '/menu-builder': { title: 'Menu Builder', subtitle: 'Build and customize your weekly menu' },
  '/meal-counts': { title: 'Meal Counts', subtitle: 'Enter production quantities' },
  '/plated': { title: 'Plated', subtitle: 'Count lock, plated counts, and late orders waiting for approval' },
  '/prep': { title: 'Prep Manager', subtitle: 'Raw weight to prep, cooked weight to log' },
  '/plating': { title: 'Plating', subtitle: 'Portion guide for kitchen staff' },
  '/weights': { title: 'Weights', subtitle: 'Weigh in cooked qty — back-calculate plating portions' },
  '/customs': { title: 'Customs', subtitle: 'Per-dish custom plating — exactly what to plate' },
  '/customer-labels': { title: 'Customer Labels', subtitle: 'Print one 2 × 1 label for every custom meal' },
  '/print-materials': { title: 'Print Materials', subtitle: 'Menu card, order card, reheating card, salad card, and stickers for this menu' },
  '/ordering': { title: 'Ordering', subtitle: 'Ingredient quantities and procurement' },
  '/recipes': { title: 'Recipes', subtitle: 'Production recipes and prep instructions' },
  '/components': { title: 'Component Library', subtitle: 'All proteins, vegetables, carbs, and sauces' },
  '/menu-development': { title: 'Menu Development', subtitle: 'Build weekly menus from the dish library' },
}

function PageHeader({
  onMenuClick, currentMenu, menus, onCommandPalette, isSpanish, translations,
  onToggleLanguage, translationsLoading, workspaceMode, onWorkspaceMode,
}) {
  const location = useLocation()
  const page = pageTitles[location.pathname] || pageTitles['/']
  const currentMenuData = menus.find(m => m.id === currentMenu)
  // Titles/subtitles go through the common-terms dict, which falls back to the
  // English source when a page has no entry — so pages keep their current text
  // and the ones that matter to Spanish-speaking staff (Prep) can be covered.
  const title = trLabel(isSpanish, translations, page.title)
  const subtitle = trLabel(isSpanish, translations, page.subtitle)
  if (workspaceMode === 'kitchen') return <header className="bg-white border-b border-havn-cream-300 px-4 py-2 flex items-center justify-between gap-3 sticky top-0 z-30"><h2 className="font-display text-xl text-havn-green-900">{trLabel(isSpanish, translations, 'Kitchen')}</h2><div className="flex items-center gap-2"><button className="min-h-[44px] px-3 rounded-lg border text-sm" onClick={onToggleLanguage}>{isSpanish ? 'ES' : 'EN'}</button><button className="min-h-[44px] px-3 rounded-lg border text-sm" onClick={() => onWorkspaceMode('admin')}>Admin</button></div></header>

  return (
    <header className="bg-white border-b border-havn-cream-300 px-4 lg:px-8 py-3 sticky top-0 z-30">
      <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-4 min-w-0">
        <button
          onClick={onMenuClick}
          className={`${workspaceMode !== 'admin' ? 'hidden' : 'lg:hidden'} p-2 -ml-2 text-havn-gray-500 hover:text-havn-charcoal`}
        >
          <MenuIcon className="w-5 h-5" />
        </button>
        <div className="min-w-0">
          <h2 className="font-display text-xl lg:text-2xl font-semibold text-havn-green-900">
            {title}
          </h2>
          <p className="text-sm text-havn-gray-500 mt-0.5 hidden sm:block">
            {subtitle}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 lg:gap-3">
        <div className="hidden md:flex p-1 rounded-lg bg-havn-cream-200">
          <button onClick={() => onWorkspaceMode('admin')} className={`px-3 py-1.5 rounded-md text-sm font-medium ${workspaceMode === 'admin' ? 'bg-white text-havn-green-900 shadow-sm' : 'text-havn-gray-500'}`}>Admin</button>
          <button onClick={() => onWorkspaceMode('kitchen')} className={`px-3 py-1.5 rounded-md text-sm font-medium ${workspaceMode === 'kitchen' ? 'bg-havn-green-700 text-white' : 'text-havn-gray-500'}`}>Kitchen</button>
          <button onClick={() => onWorkspaceMode('chef')} className={`px-3 py-1.5 rounded-md text-sm font-medium ${workspaceMode === 'chef' ? 'bg-havn-green-700 text-white' : 'text-havn-gray-500'}`}>Chef</button>
        </div>
        {/* Language Toggle */}
        <button
          onClick={onToggleLanguage}
          disabled={translationsLoading}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors disabled:opacity-60 disabled:cursor-wait
            ${isSpanish
              ? 'bg-havn-green-700 text-white border-havn-green-700'
              : 'text-havn-gray-500 border-havn-cream-300 hover:border-havn-gray-300'
            }`}
          title={isSpanish ? 'Switch to English' : 'Switch to Spanish'}
        >
          {translationsLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Globe className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">{isSpanish ? 'ES' : 'EN'}</span>
        </button>

        {/* Quick Search Button */}
        <button
          onClick={onCommandPalette}
          className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-sm text-havn-gray-600 bg-havn-cream-100 border border-havn-cream-300 rounded-lg hover:border-havn-gray-300 transition-colors"
        >
          <Command className="w-3.5 h-3.5" />
          <span>Search...</span>
          <kbd className="text-xs bg-white px-1.5 py-0.5 rounded border border-havn-cream-200">⌘K</kbd>
        </button>

        {currentMenuData && (
          <div className="hidden xl:block px-4 py-1.5 bg-havn-green-700 text-white text-sm font-medium rounded-full">
            {currentMenuData.name}
          </div>
        )}
      </div>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3 md:hidden">
        <div className="md:hidden flex p-1 rounded-lg bg-havn-cream-200">
          <button onClick={() => onWorkspaceMode('admin')} className={`px-3 py-1 rounded-md text-xs font-medium ${workspaceMode === 'admin' ? 'bg-white text-havn-green-900 shadow-sm' : 'text-havn-gray-500'}`}>Admin</button>
          <button onClick={() => onWorkspaceMode('kitchen')} className={`px-3 py-1 rounded-md text-xs font-medium ${workspaceMode === 'kitchen' ? 'bg-havn-green-700 text-white' : 'text-havn-gray-500'}`}>Kitchen</button>
          <button onClick={() => onWorkspaceMode('chef')} className={`px-3 py-1 rounded-md text-xs font-medium ${workspaceMode === 'chef' ? 'bg-havn-green-700 text-white' : 'text-havn-gray-500'}`}>Chef</button>
        </div>
      </div>
    </header>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN APP
// ══════════════════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════════════════
// FLOATING ACTION BUTTON MENU - Kitchen Tools
// ══════════════════════════════════════════════════════════════════════════════

function FloatingToolsMenu({ onOpenVoice, onOpenTimers, onOpenBatch }) {
  const [isOpen, setIsOpen] = useState(false)

  const tools = [
    { name: 'Voice Assistant', icon: Mic, color: 'bg-havn-green-600', action: onOpenVoice, hint: 'Hands-free recipe lookup' },
    { name: 'Kitchen Timers', icon: Timer, color: 'bg-orange-500', action: onOpenTimers, hint: 'Multi-timer management' },
    { name: 'Batch Calculator', icon: Calculator, color: 'bg-blue-500', action: onOpenBatch, hint: 'Prep list generator' },
  ]

  return (
    <div className="fixed bottom-6 right-6 z-40">
      {/* Tool Buttons - Animate up when open */}
      <div className={`absolute bottom-full right-0 mb-3 space-y-2 transition-all duration-300 ${
        isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}>
        {tools.map((tool, i) => (
          <button
            key={tool.name}
            onClick={() => { tool.action(); setIsOpen(false) }}
            className={`flex items-center gap-3 pl-4 pr-3 py-2.5 ${tool.color} text-white rounded-full shadow-elevated hover:scale-105 transition-all group`}
            style={{ transitionDelay: isOpen ? `${i * 50}ms` : '0ms' }}
          >
            <span className="text-sm font-medium whitespace-nowrap">{tool.name}</span>
            <tool.icon className="w-5 h-5" />
          </button>
        ))}
      </div>

      {/* Main FAB Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full shadow-elevated flex items-center justify-center transition-all duration-300 ${
          isOpen
            ? 'bg-havn-charcoal text-white rotate-45'
            : 'bg-gradient-to-br from-havn-green-600 to-havn-green-700 text-white hover:scale-105'
        }`}
        title="Kitchen Tools"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <Sparkles className="w-6 h-6" />
        )}
      </button>

      {/* Label when closed */}
      {!isOpen && (
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-havn-charcoal text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
          Kitchen Tools
        </span>
      )}
    </div>
  )
}

// Generate a default menu name based on the next Saturday
function getNextMenuDate() {
  const now = new Date()
  const daysUntilSat = (6 - now.getDay() + 7) % 7 || 7
  const nextSat = new Date(now)
  nextSat.setDate(now.getDate() + daysUntilSat)
  const month = nextSat.toLocaleString('en-US', { month: 'long' })
  const day = nextSat.getDate()
  return `Week of ${month} ${day}`
}

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false)
  const [voiceAssistantOpen, setVoiceAssistantOpen] = useState(false)
  const [kitchenTimersOpen, setKitchenTimersOpen] = useState(false)
  const [batchCalculatorOpen, setBatchCalculatorOpen] = useState(false)
  const [createMenuModalOpen, setCreateMenuModalOpen] = useState(false)
  const [isSpanish, setIsSpanish] = useState(() => localStorage.getItem('havn_language') === 'es')
  const [translations, setTranslations] = useState({ recipes: {}, dishes: {}, labels: {} })
  const [translationsLoading, setTranslationsLoading] = useState(false)
  const [recipes, setRecipes] = useState([])

  // Multi-city, multi-week state
  const [cities, setCities] = useState([])
  const hostedCity = cityFromHost(window.location.hostname)
  const [currentCity, setCurrentCity] = useState(() => initialCity(window.location, localStorage))
  const [menus, setMenus] = useState([])
  const [currentMenu, setCurrentMenu] = useState(() => {
    // Load saved menu from localStorage
    const saved = localStorage.getItem('havn_current_menu')
    return saved ? parseInt(saved, 10) : null
  })

  const { toast } = useToast()
  const navigate = useNavigate()
  const location = useLocation()
  const workspaceMode = location.pathname === '/kitchen' ? 'kitchen' : location.pathname === '/chef' ? 'chef' : 'admin'

  // City list + recipes are GLOBAL (not city-specific) — load once on mount so
  // they can never block or race the per-city menu load below. A slow or failed
  // cities/recipes fetch must not prevent the menu from loading.
  useEffect(() => {
    let cancelled = false
    async function loadGlobal() {
      try {
        const citiesData = await (await fetch('/api/menus/cities')).json()
        if (!cancelled) setCities(hostedCity
          ? PARTNER_CITIES.filter(city => city.id === hostedCity)
          : (Array.isArray(citiesData) ? citiesData.filter(city => CORE_CITIES.some(core => core.id === city.id)) : CORE_CITIES))
      } catch (err) {
        console.error('Failed to load cities:', err)
      }
      try {
        const recipesData = await (await fetch(`/api/recipes?city_id=${hostedCity || 'dc'}`)).json()
        if (!cancelled) setRecipes(Array.isArray(recipesData) ? recipesData : [])
      } catch (err) {
        console.error('Failed to load recipes:', err)
      }
    }
    loadGlobal()
    return () => { cancelled = true }
  }, [])

  // Menus reload whenever the city changes — and ONLY the menus list, so the menu
  // resolves in a single fast request. Cancellation stops a stale city's result
  // from overwriting a newer one when switching cities quickly.
  useEffect(() => {
    if (!currentCity) return
    let cancelled = false
    async function loadMenus() {
      try {
        const menusData = await menusAPI.list(currentCity)
        if (cancelled) return
        setMenus(Array.isArray(menusData) ? menusData : [])

        // Use the saved menu if it's valid for this city; otherwise land on the
        // most current (menus are returned DESC by week_of, so [0] is newest).
        const savedMenuId = localStorage.getItem('havn_current_menu')
        const savedMenu = savedMenuId ? menusData.find(m => m.id === parseInt(savedMenuId, 10)) : null
        if (savedMenu) {
          setCurrentMenu(savedMenu.id)
        } else {
          const mostCurrent = menusData[0]
          if (mostCurrent) {
            setCurrentMenu(mostCurrent.id)
            localStorage.setItem('havn_current_menu', mostCurrent.id.toString())
          }
        }
      } catch (err) {
        if (!cancelled) console.error('Failed to load menus:', err)
      }
    }
    loadMenus()
    return () => { cancelled = true }
  }, [currentCity])

  // Global keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCommandPaletteOpen(true)
      }
      if (e.key === '?' && !e.target.matches('input, textarea')) {
        e.preventDefault()
        setCommandPaletteOpen(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Navigation handler
  const handleNavigate = useCallback((page) => {
    const routes = {
      'dashboard': '/',
      'menu-builder': '/menu-builder',
      'meal-counts': '/meal-counts',
      'prep': '/prep',
      'kitchen': '/kitchen',
      'plating': '/plating',
      'weights': '/weights',
      'customs': '/customs',
      'ordering': '/ordering',
      'recipes': '/recipes',
      'components': '/components',
      'menu-development': '/menu-development',
      'print-materials': '/print-materials',
    }
    navigate(routes[page] || '/')
    setSidebarOpen(false)
  }, [navigate])

  // City change handler
  const handleCityChange = useCallback((cityId) => {
    if (hostedCity && cityId !== hostedCity) return
    setCurrentCity(cityId)
    localStorage.setItem('havn_current_city', cityId)
    setCurrentMenu(null)  // Reset menu when city changes
    localStorage.removeItem('havn_current_menu')  // Clear saved menu when city changes
  }, [hostedCity])

  // Back to the starter page to switch cities (sidebar logo / "Switch" click).
  const handleGoHome = useCallback(() => {
    if (hostedCity) {
      navigate('/')
      return
    }
    setCurrentCity(null)
    localStorage.removeItem('havn_current_city')
    setCurrentMenu(null)
    localStorage.removeItem('havn_current_menu')
    navigate('/')
  }, [navigate, hostedCity])

  // Language toggle handler — persists to localStorage so staff don't
  // have to flip it every session.
  const handleToggleLanguage = useCallback(() => {
    setIsSpanish(prev => {
      const next = !prev
      localStorage.setItem('havn_language', next ? 'es' : 'en')
      return next
    })
  }, [])

  // Turn the visible kitchen UI Spanish as soon as the small, static label
  // dictionary arrives. Recipe/dish translations are filled in afterward in
  // the background; a slow translation provider must never trap the language
  // button behind an endless spinner.
  useEffect(() => {
    let cancelled = false

    if (!isSpanish) {
      // Only reset if we actually had translations — avoid a render on every
      // unrelated effect trigger when English is already the active mode.
      setTranslations(prev => (
        Object.keys(prev.recipes).length === 0 &&
        Object.keys(prev.dishes).length === 0 &&
        Object.keys(prev.labels).length === 0
          ? prev
          : { recipes: {}, dishes: {}, labels: {} }
      ))
      setTranslationsLoading(false)
      return () => {}
    }
    if (!currentCity) return () => {}

    setTranslationsLoading(true)
    ;(async () => {
      try {
        const labels = await translateAPI.commonTerms('spanish')
        if (cancelled) return
        setTranslations((prev) => ({ ...prev, labels: labels || {} }))
      } catch (err) {
        if (!cancelled) {
          console.error('Translation labels failed:', err)
          toast({ type: 'error', message: 'Translation failed — showing English.' })
        }
      } finally {
        if (!cancelled) setTranslationsLoading(false)
      }

      const [recipesResult, dishesResult] = await Promise.allSettled([
        translateAPI.recipesBulk(currentCity, 'spanish'),
        translateAPI.dishesBulk(currentCity, 'spanish'),
      ])
      if (cancelled) return
      const recipesRes = recipesResult.status === 'fulfilled' ? recipesResult.value : null
      const dishesRes = dishesResult.status === 'fulfilled' ? dishesResult.value : null
      setTranslations((prev) => ({
        ...prev,
        recipes: recipesRes?.recipes || prev.recipes,
        dishes: dishesRes?.dishes || prev.dishes,
      }))
    })()

    return () => { cancelled = true }
  }, [isSpanish, currentCity, toast])

  // Create new menu
  const handleCreateMenu = useCallback(() => {
    setCreateMenuModalOpen(true)
  }, [])

  const handleCreateMenuSubmit = useCallback(async (name) => {
    try {
      // Derive week_of date (next Saturday) for the menu
      const weekOf = new Date()
      const daysUntilSat = (6 - weekOf.getDay() + 7) % 7 || 7
      weekOf.setDate(weekOf.getDate() + daysUntilSat)
      const weekOfStr = weekOf.toISOString().split('T')[0] // YYYY-MM-DD

      const newMenu = await menusAPI.create({
        city_id: currentCity,
        name: name,
        week_of: weekOfStr,
      })
      setMenus(prev => [newMenu, ...prev])
      setCurrentMenu(newMenu.id)
      localStorage.setItem('havn_current_menu', newMenu.id.toString())
      toast({ type: 'success', message: `Created menu: ${name}` })
    } catch (err) {
      console.error('Failed to create menu:', err)
      toast({ type: 'error', message: `Failed to create menu: ${err.message}` })
    }
  }, [currentCity, toast])

  // App context value
  const appContextValue = {
    currentCity,
    currentMenu,
    cities,
    menus,
    recipes,
    isSpanish,
    translations,
    translationsLoading,
    setCurrentMenu,
    setCurrentCity: handleCityChange,
  }

  // No city chosen yet → starter page (the only place to pick a city).
  if (!currentCity) {
    return <CityPicker cities={cities} onPick={handleCityChange} />
  }

  return (
    <AppContext.Provider value={appContextValue}>
      <div className="flex min-h-screen bg-havn-cream-100">
        {workspaceMode === 'admin' && <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          currentCity={currentCity}
          cities={cities}
          hostedCity={hostedCity}
          onGoHome={handleGoHome}
          currentMenu={currentMenu}
          menus={menus}
          onMenuChange={(menuId) => {
            setCurrentMenu(menuId)
            localStorage.setItem('havn_current_menu', menuId.toString())
          }}
          onCreateMenu={handleCreateMenu}
        />}

        <div className={`flex-1 min-w-0 flex flex-col min-h-screen ${workspaceMode === 'admin' ? 'lg:ml-72' : ''}`}>
          <PageHeader
            onMenuClick={() => setSidebarOpen(true)}
            currentMenu={currentMenu}
            menus={menus}
            onCommandPalette={() => setCommandPaletteOpen(true)}
            isSpanish={isSpanish}
            translations={translations}
            onToggleLanguage={handleToggleLanguage}
            translationsLoading={translationsLoading}
            workspaceMode={workspaceMode}
            onWorkspaceMode={(mode) => navigate(mode === 'kitchen' ? '/kitchen' : mode === 'chef' ? '/chef' : '/')}
          />

          <main className="flex-1 p-4 lg:p-8 overflow-auto">
            <Routes>
              {/* City entry points: /dc forces DC, /sd forces SD. The city is
                  already set by the currentCity useState initializer based on
                  window.location.pathname; these routes just clean up the URL
                  so the user lands on the dashboard at /. */}
              <Route path="/dc" element={<Navigate to="/" replace />} />
              <Route path="/sd" element={<Navigate to="/" replace />} />
              <Route path="/" element={<OperationsHome />} />
              <Route path="/kitchen" element={<Kitchen key="kitchen" />} />
              <Route path="/chef" element={<Kitchen key="chef" chefMode />} />
              <Route path="/kitchen-overview" element={<AdminKitchen />} />
              <Route path="/menu-builder" element={<MenuBuilder />} />
              <Route path="/meal-counts" element={<MealCounts />} />
              <Route path="/plated" element={<Plated />} />
              <Route path="/prep" element={<Prep />} />
              <Route path="/plating" element={<Plating />} />
              <Route path="/weights" element={<Weights />} />
              <Route path="/customs" element={<Customs />} />
              <Route path="/customer-labels" element={<CustomerLabels />} />
              <Route path="/print-materials" element={<PrintMaterials />} />
              <Route path="/ordering" element={<Ordering />} />
              <Route path="/recipes" element={<Recipes />} />
              <Route path="/components" element={<ComponentLibrary onNavigate={handleNavigate} />} />
              <Route path="/menu-development" element={<MenuDevelopment />} />
            </Routes>
          </main>
        </div>

        <Toast />

        {/* Command Palette */}
        <CommandPalette
          isOpen={commandPaletteOpen}
          onClose={() => setCommandPaletteOpen(false)}
          onNavigate={handleNavigate}
          recipes={recipes}
          currentMenu={currentMenu}
        />

        {/* Voice Assistant */}
        {voiceAssistantOpen && (
          <VoiceAssistant
            recipes={recipes}
            onClose={() => setVoiceAssistantOpen(false)}
          />
        )}

        {/* Kitchen Timers */}
        {kitchenTimersOpen && (
          <KitchenTimers
            onClose={() => setKitchenTimersOpen(false)}
          />
        )}

        {/* Batch Calculator */}
        {batchCalculatorOpen && (
          <BatchCalculator
            onClose={() => setBatchCalculatorOpen(false)}
          />
        )}

        {/* Create Menu Modal */}
        <InputModal
          isOpen={createMenuModalOpen}
          onClose={() => setCreateMenuModalOpen(false)}
          onSubmit={handleCreateMenuSubmit}
          title="Create New Menu"
          message={`Enter a name for this week's menu (e.g., '${getNextMenuDate()}')`}
          placeholder={getNextMenuDate()}
          defaultValue={getNextMenuDate()}
          submitText="Create Menu"
        />
      </div>
    </AppContext.Provider>
  )
}

export default function App() {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  )
}
